import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HandCoins } from "lucide-react";
import { api, apiError, brl, dataBR } from "@/lib/api";
import { EmptyState, ErrorState, Loading, Surface } from "@/components/ds";

const Rows = ({ rows, testid, tone }) => (
  <div className="divide-y divide-white/5" data-testid={testid}>
    {rows.map((r) => (
      <div
        key={r.order_id || r.closure_id}
        className="flex flex-wrap items-center gap-2 py-3"
        data-testid={`receivable-${r.order_id || r.closure_id}`}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {r.customer_name || r.partner_name}
            <span className="ml-2 font-num text-[11px] text-muted-foreground">{r.number}</span>
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {r.created_at ? dataBR(r.created_at) : ""}
            {r.origin_label ? ` · ${r.origin_label}` : ""}
            {r.period_label ? ` · ${r.period_label}` : ""}
            {r.payment_method ? ` · ${r.payment_method}` : ""}
            {r.status_label ? ` · ${r.status_label}` : ""}
          </p>
        </div>
        <span className={`shrink-0 font-num text-sm font-semibold ${tone}`}>{brl(r.total)}</span>
      </div>
    ))}
  </div>
);

export function FinanceReceivables() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    api
      .get("/finance/receivables")
      .then(({ data }) => setData(data))
      .catch((e) => setError(apiError(e)));
  }, []);

  useEffect(load, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Carregando recebimentos..." />;

  const empty =
    data.customers.waiting.length === 0 &&
    data.customers.informed.length === 0 &&
    data.partners.rows.length === 0;

  return (
    <div className="space-y-5" data-testid="finance-receivables">
      <div className="grid gap-3 sm:grid-cols-3">
        <Surface className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Total a receber
          </p>
          <p className="mt-1 font-num text-xl font-semibold" data-testid="receivable-total">
            {brl(data.total)}
          </p>
        </Surface>
        <Surface className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Clientes</p>
          <p
            className="mt-1 font-num text-xl font-semibold text-amber-300"
            data-testid="receivable-customers-total"
          >
            {brl(data.customers.total)}
          </p>
        </Surface>
        <Surface className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Parceiros</p>
          <p
            className="mt-1 font-num text-xl font-semibold text-brand"
            data-testid="receivable-partners-total"
          >
            {brl(data.partners.total)}
          </p>
        </Surface>
      </div>

      {empty ? (
        <EmptyState
          icon={HandCoins}
          title="Nada a receber"
          description="Todos os pedidos e fechamentos estão com pagamento confirmado."
        />
      ) : (
        <div className="space-y-4">
          {data.customers.waiting.length > 0 && (
            <Surface className="p-5">
              <h3 className="text-sm font-medium">Aguardando pagamento</h3>
              <p className="mb-2 text-xs text-muted-foreground">
                {brl(data.customers.waiting_total)} · nenhum aviso de pagamento ainda
              </p>
              <Rows rows={data.customers.waiting} testid="waiting-rows" tone="text-amber-300" />
            </Surface>
          )}

          {data.customers.informed.length > 0 && (
            <Surface className="border-amber-500/30 p-5">
              <h3 className="text-sm font-medium">Cliente informou o pagamento</h3>
              <p className="mb-2 text-xs text-muted-foreground">
                {brl(data.customers.informed_total)} · aviso do cliente, ainda{" "}
                <strong className="text-foreground">não confirmado</strong>. Confirme em Pedidos
                para entrar em Recebido.
              </p>
              <Rows rows={data.customers.informed} testid="informed-rows" tone="text-amber-300" />
              <Link
                to="/pedidos"
                className="mt-3 inline-flex text-xs font-medium text-brand"
                data-testid="go-to-orders-link"
              >
                Abrir Pedidos →
              </Link>
            </Surface>
          )}

          {data.partners.rows.length > 0 && (
            <Surface className="p-5">
              <h3 className="text-sm font-medium">Parceiros a receber</h3>
              <p className="mb-2 text-xs text-muted-foreground">
                {brl(data.partners.total)} em fechamentos de consignado em aberto
              </p>
              <Rows rows={data.partners.rows} testid="partner-receivable-rows" tone="text-brand" />
              <Link
                to="/parceiros"
                className="mt-3 inline-flex text-xs font-medium text-brand"
                data-testid="go-to-partners-link"
              >
                Abrir Parceiros →
              </Link>
            </Surface>
          )}
        </div>
      )}
    </div>
  );
}
